import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { DialogTitle } from '../dialog/dialog';
import { Close } from '@components/atoms';
import { cn } from '@utils';

type DrawerPosition = 'left' | 'right';

interface IDrawerBaseProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: string;
	subtitle?: React.ReactNode;
	position?: DrawerPosition;
	width?: string;
	modal?: boolean;
	showCloseButton?: boolean;
	/**
	 * Whether interacting outside the drawer dismisses it. Pass false to keep the panel open
	 * through any outside click, or a predicate receiving the interaction target to decide per
	 * click (e.g. keep open for grid-row clicks that swap the panel, close for everything else).
	 * @default true
	 */
	closeOnInteractOutside?: boolean | ((target: EventTarget | null) => boolean);
	closeDisabled?: boolean;
	className?: string;
	'aria-label': string;
}

type DrawerProps =
	| (IDrawerBaseProps & { showCloseButton?: true; closeAriaLabel: string })
	| (IDrawerBaseProps & { showCloseButton: false });

const positionClasses: Record<DrawerPosition, string> = {
	left: 'left-0 top-0 h-dvh data-[state=open]:slide-in-from-left-full data-[state=closed]:slide-out-to-left-full',
	right: 'right-0 top-0 h-dvh data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full',
};

const overlayClasses =
	'fixed inset-0 z-[999] bg-bgOverlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0';

const Drawer: React.FC<React.PropsWithChildren<DrawerProps>> = props => {
	const {
		open,
		onOpenChange,
		title,
		subtitle,
		position = 'right',
		width,
		modal = true,
		showCloseButton = true,
		closeOnInteractOutside = true,
		closeDisabled = false,
		className,
		'aria-label': ariaLabel,
		children,
	} = props;
	const closeAriaLabel = props.showCloseButton === false ? undefined : props.closeAriaLabel;
	const hasHeader = Boolean(title) || Boolean(subtitle);

	return (
		<DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={modal}>
			<DialogPrimitive.Portal>
				{Boolean(modal) && <DialogPrimitive.Overlay className={overlayClasses} data-testid="drawer-overlay" />}
				<DialogPrimitive.Content
					data-testid="drawer-content"
					onEscapeKeyDown={event => {
						if (closeDisabled) {
							event.preventDefault();
						}
					}}
					onInteractOutside={event => {
						const shouldClose =
							!closeDisabled &&
							(typeof closeOnInteractOutside === 'function'
								? closeOnInteractOutside(event.target)
								: closeOnInteractOutside);
						if (!shouldClose) {
							event.preventDefault();
						}
					}}
					aria-modal={modal || undefined}
					aria-describedby={undefined}
					style={{ '--drawer-width': width } as React.CSSProperties}
					className={cn(
						'fixed z-[1000] flex w-full flex-col bg-white shadow-lg duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in md:w-[var(--drawer-width)]',
						positionClasses[position],
						className
					)}
				>
					{!title ? <DialogPrimitive.Title className="sr-only">{ariaLabel}</DialogPrimitive.Title> : null}
					{hasHeader && (
						<div
							className="flex flex-col gap-1 border-b border-borderSeparator py-[16px] pr-10 pl-[24px]"
							data-testid="drawer-header"
						>
							{Boolean(title) && <DialogTitle data-testid="drawer-title">{title}</DialogTitle>}
							{Boolean(subtitle) && (
								<span className="text-xs text-textMediumGray" data-testid="drawer-subtitle">
									{subtitle}
								</span>
							)}
						</div>
					)}
					{Boolean(showCloseButton) && closeAriaLabel ? (
						<DialogPrimitive.Close
							data-testid="drawer-close-button"
							aria-label={closeAriaLabel}
							disabled={closeDisabled}
							className="absolute top-0 right-0 z-10 flex h-8 w-8 items-center justify-center bg-transparent text-transparent transition-colors focus:outline-none disabled:cursor-not-allowed"
						>
							<Close
								className={cn(
									'h-4 w-4 text-secondary',
									closeDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:text-primary'
								)}
							/>
						</DialogPrimitive.Close>
					) : null}
					{children}
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
};

export { Drawer };
export type { DrawerProps, DrawerPosition };
